import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from "react";
import { useForm, type UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import {
  formDataAtom,
  formSessionAtom,
  setCustomerIdAtom,
  setFormIdAtom,
  stepDataStateAtom,
} from "@/state/formSession";
import { URLS } from "@/constants/urls";
import { type StepTitle, type StepKey, STEP_KEYS } from "@/types/Form";
import { useStepQuery, useUpsertStepMutation } from "@/hooks";
import {
  type FormStep,
  getMultiStepFormSchema,
  type MultiStepFormType,
  getStepConfigs,
} from "@/utils/formConfig";
import { hydrateFormDataFromApi } from "@/utils/hydrateFormData";

interface MultiStepFormProviderValue {
  currentStep: FormStep;
  currentStepIndex: number;
  steps: FormStep[];
  isLastStep: boolean;
  isFirstStep: boolean;
  nextStep: () => Promise<void>;
  previousStep: () => void;
  submit: (data: MultiStepFormType) => void;
  resetStep: (stepKey: StepKey) => void;
  validateStepAndRun: (
    stepKey: StepKey,
    callback?: () => void | Promise<void>,
    options?: { skipCalculations?: boolean },
  ) => Promise<void> | void;
  form: UseFormReturn<MultiStepFormType>;
  triggeredSteps: Record<StepTitle, boolean>;
  isLoading?: boolean;
}

// eslint-disable-next-line react-refresh/only-export-components
export const MultiStepFormContext = createContext<MultiStepFormProviderValue | null>(null);

export const MultiStepFormProvider: FC<{
  children: ReactNode;
  steps: FormStep[];
  isSummary?: boolean;
  testInitialStep?: number;
}> = ({ children, steps, isSummary, testInitialStep = 0 }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [storedFormData, setStoredFormData] = useAtom(formDataAtom);
  const setStepDataState = useSetAtom(stepDataStateAtom);
  const queryFormId = searchParams.get("formId");
  const canHydrateFromQuery = location.pathname === URLS.HOME;
  const hasResultsQuery = canHydrateFromQuery && Boolean(queryFormId);

  const getInitialStep = useCallback(() => {
    if (hasResultsQuery) return steps.length - 1;

    if (typeof testInitialStep === "number") return testInitialStep;
    const stepParam = searchParams.get("step");
    if (stepParam !== null) {
      const parsed = Number(stepParam);
      if (!isNaN(parsed) && parsed >= 0 && parsed < steps.length) {
        return parsed;
      }
    }

    return 0;
  }, [hasResultsQuery, searchParams, steps.length, testInitialStep]);

  const [currentStepIndex, setCurrentStepIndex] = useState(getInitialStep());

  useEffect(() => {
    if (hasResultsQuery) {
      setCurrentStepIndex(steps.length - 1);
      return;
    }

    const stepParam = searchParams.get("step");
    if (stepParam !== null) {
      const parsed = Number(stepParam);
      if (!isNaN(parsed) && parsed >= 0 && parsed < steps.length) {
        setCurrentStepIndex(parsed);
      }
    }
  }, [hasResultsQuery, searchParams, steps.length]);

  const { formId, customerId } = useAtomValue(formSessionAtom);
  const setFormId = useSetAtom(setFormIdAtom);
  const setCustomerId = useSetAtom(setCustomerIdAtom);
  const hydrationStateRef = useRef<Record<string, "idle" | "loading" | "done">>({});

  const [triggeredSteps, setTriggeredSteps] = useState<Record<StepTitle, boolean>>(
    {} as Record<StepTitle, boolean>,
  );

  const isEmpty = !storedFormData || Object.keys(storedFormData).length === 0;
  const currentStep = steps[currentStepIndex];

  const multiStepFormSchema = useMemo(() => getMultiStepFormSchema(t), [t]);

  const form = useForm<MultiStepFormType>({
    resolver: zodResolver(multiStepFormSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: storedFormData ?? {},
  });

  const stepConfigs = useMemo(() => getStepConfigs(), []);

  useEffect(
    function hydrate() {
      const targetFormId = canHydrateFromQuery ? (queryFormId ?? formId) : formId;
      if (!targetFormId) return;
      const hydrationState = hydrationStateRef.current[targetFormId] ?? "idle";
      if (hydrationState !== "idle") return;
      if (storedFormData && Object.keys(storedFormData).length > 0 && formId === targetFormId)
        return;

      const hydrateFromApi = async () => {
        hydrationStateRef.current[targetFormId] = "loading";
        if (formId !== targetFormId) {
          setFormId(targetFormId);
        }

        const hydrated = await hydrateFormDataFromApi(targetFormId, stepConfigs);

        if (hydrated.customer && hydrated.customer.id !== customerId) {
          setCustomerId(hydrated.customer.id);
        }

        form.reset(hydrated.data as MultiStepFormType);
        setStoredFormData(hydrated.data);
        setStepDataState(hydrated.stepDataState);
        hydrationStateRef.current[targetFormId] = "done";
      };

      void hydrateFromApi().catch(() => {
        hydrationStateRef.current[targetFormId] = "idle";
      });
    },
    [
      customerId,
      canHydrateFromQuery,
      form,
      formId,
      queryFormId,
      setCustomerId,
      setFormId,
      setStepDataState,
      setStoredFormData,
      stepConfigs,
      storedFormData,
    ],
  );

  const stepKey = STEP_KEYS[currentStepIndex];
  const stepConfig = stepConfigs[stepKey];

  useStepQuery(stepConfig, formId, customerId, form);

  const { mutateAsync: upsertStepData, isPending } = useUpsertStepMutation(
    stepConfigs,
    formId,
    customerId,
    setFormId,
    setCustomerId,
    form,
    t,
  );

  const nextStep = async () => {
    const isValid = await form.trigger(stepKey);

    setTriggeredSteps(prev => ({ ...prev, [currentStep.title]: true }));
    if (!isValid) return;

    await upsertStepData({ stepKey });

    setStoredFormData(form.getValues());

    if (currentStepIndex < steps.length - 1) {
      const otherKeys = STEP_KEYS.filter(k => k !== stepKey);
      form.clearErrors(otherKeys);

      setCurrentStepIndex(prev => prev + 1);
    }
  };

  const previousStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  };

  const submit = (data: MultiStepFormType) => {
    setStoredFormData(data);
  };

  const resetStep = (k: StepKey) => {
    const defaultStepData = storedFormData?.[k];
    if (!defaultStepData) return;

    form.reset({
      ...form.getValues(),
      [k]: defaultStepData,
    });
  };

  const validateStepAndRun = async (
    k: StepKey,
    callback?: () => void | Promise<void>,
    options?: { skipCalculations?: boolean },
  ) => {
    const stepIndex = STEP_KEYS.indexOf(k);
    const step = steps[stepIndex];

    const isValid = await form.trigger(k);

    setTriggeredSteps(prev => ({
      ...prev,
      [step.title]: true,
    }));

    if (!isValid) return;

    await upsertStepData({ stepKey: k, options });
    setStoredFormData(form.getValues());

    if (callback) {
      await callback();
    }
  };

  if (isSummary && isEmpty && !formId) {
    return <Navigate to={URLS.HOME} replace />;
  }

  return (
    <MultiStepFormContext.Provider
      value={{
        currentStepIndex,
        currentStep,
        nextStep,
        previousStep,
        submit,
        steps,
        isLastStep: currentStepIndex === steps.length - 1,
        isFirstStep: currentStepIndex === 0,
        form,
        triggeredSteps,
        resetStep,
        validateStepAndRun,
        isLoading: isPending,
      }}
    >
      {children}
    </MultiStepFormContext.Provider>
  );
};
